import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import logo from '../../assets/logo.png';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const { getRecaptchaToken } = await import('../../services/recaptcha');
      const recaptchaToken = await getRecaptchaToken('forgot_password');
      const response = await authService.forgotPassword(email, recaptchaToken);
      if (response.success) {
        setIsSubmitted(true);
        setMessage('Password reset instructions have been sent to your email.');
      } else {
        setError(response.error || 'Failed to send reset instructions');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send reset instructions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex flex-col items-center">
            <Link to="/" className="mb-8">
              <div className="bg-white p-3 rounded-2xl shadow-lg">
                <img src={logo} alt="12and22" className="h-10 w-auto" />
              </div>
            </Link>
            
            <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
              <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
              <span className="text-sm font-semibold text-gray-700">Password Recovery</span>
            </div>
          </div>
          
          <h1 className="text-4xl font-black text-gray-900 mb-4">
            Reset your <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">password</span>
          </h1>
          
          <p className="text-gray-600">
            Remember your password?{' '}
            <Link
              to="/login"
              className="font-semibold text-primary-navy hover:text-orange-500 transition-colors duration-200"
            >
              Sign in here
            </Link>
          </p>
        </div>

        {!isSubmitted ? (
          /* Password Reset Form Card */
          <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-8 relative overflow-hidden">
            {/* Subtle background overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent"></div>
            
            <div className="relative z-10">
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-500"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    We'll send password reset instructions to this email address.
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-red-400 mr-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary-navy hover:bg-primary-800 text-white py-4 px-6 rounded-xl font-bold text-lg transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending instructions...
                    </div>
                  ) : (
                    'Send Reset Instructions'
                  )}
                </button>
              </form>
            </div>
          </div>
        ) : (
          /* Success Message Card */
          <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-8 relative overflow-hidden">
            {/* Subtle background overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent"></div>
            
            <div className="relative z-10">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <svg className="w-8 h-8 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                
                <h2 className="text-2xl font-black text-gray-900 mb-4">Check Your Email</h2>
                <p className="text-gray-600 mb-2">{message}</p>
                <p className="text-sm text-gray-500 mb-8">
                  Please check your email and follow the instructions to reset your password.
                </p>
                
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center bg-primary-navy hover:bg-primary-800 text-white py-3 px-6 rounded-xl font-bold transition-all duration-200 shadow-lg"
                >
                  Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;